// The data contract shared by the dynamic node renderer and the prebuilt nodes.
//
// Graph node `data` is FLAT: every parameter value lives directly on `data` under its parameter
// name (`data.model`, `data.inputs`, `data.inputCount`, `data.code`, `data.dataSet`, …), exactly
// as the prebuilt components store it. The dynamic renderer must read and write these same flat
// keys, so that replacing a node's prebuilt component with the dynamic renderer leaves the graph
// definition byte-for-byte unchanged — no `values` wrapper, no nested `default_data`, no reshape.

/**
 * Read a parameter's current value: flat `data[name]`, then the definition's default, then the
 * parameter's own default.
 */
export const resolveParameterValue = ({ parameter, data = {}, defaultData = {} }) => {
  const name = parameter?.name;
  if (name && data[name] !== undefined) return data[name];
  if (name && defaultData[name] !== undefined) return defaultData[name];
  return parameter?.default;
};

/**
 * Whether a parameter is currently shown.
 *
 * - `visible_when` (or `visibleWhen`): `{field: value}` / `{field: [values]}` — all must match.
 * - `visible_when_any` (or `visibleWhenAny`): either `{field: value}` / `{field: [values]}` (any
 *   field matches) or a plain array, which is compared against the field named by `visible_when`
 *   (e.g. `visible_when: {operation: "send_message"}, visible_when_any: ["update_message"]`).
 *
 * The two are alternatives: the field shows if `visible_when` matches **or** `visible_when_any`
 * does. Conditions read flat data, falling back to catalog defaults (so `operation` initialises
 * from `default_data`).
 */
export const isParameterVisible = (parameter = {}, data = {}, defaultData = {}) => {
  const matches = (field, expected) => {
    const actual = data?.[field] ?? defaultData?.[field];
    const allowed = Array.isArray(expected) ? expected : [expected];
    return allowed.some((candidate) => candidate === actual);
  };

  const allOf = parameter?.visible_when ?? parameter?.visibleWhen ?? null;
  const anyOf = parameter?.visible_when_any ?? parameter?.visibleWhenAny ?? null;

  if (allOf === null && anyOf === null) return true;

  if (allOf && typeof allOf === "object") {
    if (Object.entries(allOf).every(([field, expected]) => matches(field, expected))) return true;
  }

  if (Array.isArray(anyOf)) {
    const fields = allOf && typeof allOf === "object" ? Object.keys(allOf) : ["operation"];
    return fields.some((field) => anyOf.some((candidate) => matches(field, candidate)));
  }

  if (anyOf && typeof anyOf === "object") {
    return Object.entries(anyOf).some(([field, expected]) => matches(field, expected));
  }

  return false;
};

/**
 * The `key` a dependent remote picker sends: the value of the last populated `depends_on` field
 * (e.g. Slack channels depend on `clientKey`).
 */
export const resolveDependencyKey = (controlOptions = {}, data = {}) => {
  const dependsOn = Array.isArray(controlOptions?.depends_on) ? controlOptions.depends_on : [];
  const values = dependsOn
    .map((field) => data?.[field])
    .filter((candidate) => candidate !== undefined && candidate !== null && candidate !== "");

  return values.length > 0 ? String(values[values.length - 1]) : null;
};

/** A write patch is a single flat key, never a nested object. */
export const flatPatch = (name, value) => ({ [name]: value });

/**
 * The input list a DynamicNode renders.
 *
 * An explicit `inputs` array always wins — including `[]`, which means the node genuinely has
 * no inputs (any node may have zero). Only when `inputs` is absent entirely does the node fall
 * back to the catalog's `default_data.inputs`.
 */
export const resolveInputArray = ({ data = {}, inputSource = "inputs", defaultInputs = [] }) => {
  const value = data?.[inputSource];
  if (Array.isArray(value)) return value;
  return Array.isArray(defaultInputs) ? defaultInputs : [];
};

/** The catalog's default inputs for a dynamic-input node (`default_data[input_source]`). */
export const defaultInputsFor = (definition = {}) => {
  const source = definition?.handles?.inputSource;
  const defaults = source ? definition?.defaultData?.[source] : null;
  return Array.isArray(defaults) ? defaults : [];
};

/** Grow/shrink an input list, preserving existing entries and their ids (e.g. `input-1`, `input`). */
export const growInputs = (current, count) => {
  const next = [];
  for (let index = 0; index < count; index += 1) {
    next.push(current[index] ?? { id: `input-${index + 1}`, label: index === 0 ? "Input" : `Input ${index + 1}` });
  }
  return next;
};

/** Rename one input, leaving every other entry (and its id) untouched. */
export const renameInput = (current, index, label) =>
  current.map((item, i) => (i === index ? { ...item, label } : item));

/**
 * Derive the concrete ports a node exposes from its catalog definition.
 *
 * - static: `handles.inputs` / `handles.outputs`, one React Flow handle per entry (port.id);
 * - dynamic inputs: the node's own input list (`input_source`, defaulting to `default_data`);
 * - dynamic outputs: the node's branches (`output_source` — labels or routes) plus the
 *   `fallback_handle`; when `output_handle_mode` is `label`, the label *is* the handle id.
 *
 * Nothing here depends on a hardcoded per-type map, so a node the server adds tomorrow renders
 * its ports correctly.
 */
export const resolveHandles = (definition = {}, data = {}) => {
  const handles = definition?.handles ?? {};

  const toPort = (handle, side) => ({
    id: handle?.id == null ? null : String(handle.id),
    label: String(handle?.label ?? handle?.id ?? (side === "target" ? "input" : "output")),
    position: side === "target" ? "left" : "right",
  });

  const staticTargets = (handles.inputs ?? []).map((handle) => toPort(handle, "target"));
  const staticSources = (handles.outputs ?? []).map((handle) => toPort(handle, "source"));

  // Inputs.
  let targets = staticTargets;

  if (handles.dynamicInputs && handles.inputSource) {
    const inputs = resolveInputArray({
      data,
      inputSource: handles.inputSource,
      defaultInputs: defaultInputsFor(definition),
    });

    const built = inputs.map((input, index) => {
      const id = typeof input === "object" ? input.id ?? `input-${index + 1}` : String(input);
      const label = typeof input === "object" ? input.label ?? input.id : String(input);

      return {
        id: id == null ? null : String(id),
        label: String(label ?? id),
        position: "left",
      };
    });

    targets = built.length > 0 ? built : staticTargets;
  }

  // Outputs.
  let sources = staticSources;

  if (handles.dynamicOutputs && handles.outputSource) {
    const branches = Array.isArray(data?.[handles.outputSource]) ? data[handles.outputSource] : [];

    const built = branches.map((branch, index) => {
      const isObject = branch && typeof branch === "object";

      const value = isObject
        ? handles.outputHandleMode === "label"
          ? branch.label
          : branch.id ?? branch.label
        : String(branch);

      const label = isObject ? branch.label ?? branch.id : String(branch);

      return {
        id: value == null ? `output-${index + 1}` : String(value),
        label: String(label ?? value),
        position: "right",
      };
    });

    if (handles.fallbackHandle) {
      built.push({ id: handles.fallbackHandle, label: handles.fallbackHandle, position: "right" });
    }

    sources = built.length > 0 ? built : staticSources;
  }

  return { targets, sources };
};

/** Backwards-compatible alias: the handles a DynamicNode renders. */
export const renderedHandles = (definition = {}, data = {}) => resolveHandles(definition, data);

/**
 * Rewrite edge handles that don't match the ports their node renders.
 *
 * Uses the same `resolveHandles` as the renderer, so legacy handle names from older graphs
 * (`llm-output`, `dataSet-input`, …) are mapped onto the catalog's port ids instead of
 * disappearing. Unknown node types are left alone.
 */
export const normalizeGraphHandlesFromDefinitions = (graph = {}, definitionsByType = {}) => {
  const nodes = Array.isArray(graph.nodes) ? graph.nodes : [];
  const edges = Array.isArray(graph.edges) ? graph.edges : [];
  const byId = new Map(nodes.map((node) => [String(node.id), node]));

  const portsOf = (node) => {
    const definition = node ? definitionsByType[node.type] : null;
    return definition ? resolveHandles(definition, node.data ?? {}) : null;
  };

  const resolveHandle = (node, side, current) => {
    const ports = portsOf(node);
    if (!ports) return current;

    const list = side === "source" ? ports.sources : ports.targets;
    if (!list || list.length === 0) return current;

    const wanted = current === undefined || current === null ? null : String(current);
    const match = list.find((port) => (port.id ?? null) === wanted);

    if (match) return match.id ?? null;

    // Unrecognised handle: fall back to the node's first port on that side.
    return list[0].id ?? null;
  };

  const normalizedEdges = edges.map((edge) => {
    if (!edge) return edge;

    const sourceHandle = resolveHandle(byId.get(String(edge.source)), "source", edge.sourceHandle);
    const targetHandle = resolveHandle(byId.get(String(edge.target)), "target", edge.targetHandle);

    if (sourceHandle === (edge.sourceHandle ?? null) && targetHandle === (edge.targetHandle ?? null)) {
      return edge;
    }

    return { ...edge, sourceHandle: sourceHandle ?? null, targetHandle: targetHandle ?? null };
  });

  return { ...graph, nodes, edges: normalizedEdges };
};

/** The fixed size (px) of an entry/output node card — square and deliberately small. */
export const COMPACT_NODE_SIZE = 110;

/**
 * Entry and output nodes render as a compact square, not a full parameter card. Terminal nodes
 * (the Text Output and the ChromaDB Store sink) count as outputs regardless of category.
 */
export const isCompactNode = (definition = {}) => {
  if (definition?.isEntry || definition?.isTerminal) return true;
  const category = String(definition?.category ?? "").toLowerCase();
  return category === "entry" || category === "output";
};

/**
 * A rough height for a rendered DynamicNode, used by the auto-layout to space nodes without
 * overlapping. The real height is content-driven, so this errs on the generous side.
 */
export const estimateNodeHeight = (definition = {}, data = {}) => {
  if (isCompactNode(definition)) return COMPACT_NODE_SIZE;

  const parameters = definition.parameters ?? [];
  const basic = parameters.filter((parameter) => !parameter?.advanced && parameter?.name !== "label").length;
  const advanced = parameters.filter((parameter) => parameter?.advanced).length;
  const handles = definition.handles ?? {};

  let height = 70; // padding + header
  if (definition.description) height += 46;
  if ((handles.inputs ?? []).length || (handles.outputs ?? []).length || handles.dynamicInputs) height += 30;
  height += basic * 78;
  if (advanced > 0) height += 30;

  if (handles.dynamicInputs && handles.inputSource) {
    const inputs = resolveInputArray({
      data,
      inputSource: handles.inputSource,
      defaultInputs: defaultInputsFor(definition),
    });
    height += 34 + inputs.length * 40;
  }

  return Math.max(110, Math.round(height));
};

const dynamicNodeData = {
  resolveParameterValue,
  flatPatch,
  resolveInputArray,
  growInputs,
  renameInput,
  resolveHandles,
  renderedHandles,
  normalizeGraphHandlesFromDefinitions,
  defaultInputsFor,
  isCompactNode,
  COMPACT_NODE_SIZE,
  estimateNodeHeight,
  isParameterVisible,
  resolveDependencyKey,
};

export default dynamicNodeData;
